<template>
  <el-dropdown ref="dropdownRef" :teleported="true" trigger="click" class="w-full" :hide-on-click="false">
    <div class="emoji-trigger">
      <span class="text-[24px]">{{ displayEmoji || '?' }}</span>
      <el-icon size="20"><ChevronDown /></el-icon>
    </div>
    <template #dropdown>
      <div class="emoji-picker-container">
        <div v-if="modelValue" class="flex justify-between items-center mb-2 pb-2 border-b">
          <div class="flex items-center gap-2">
            <span class="text-[24px]">{{ displayEmoji }}</span>
            <span class="text-[14px] text-gray-500">{{ t('common.current_selection') }}</span>
          </div>
          <x-button color="#E53030" class="button-square !w-7 !h-7 !min-w-0 !rounded-md" @click="clearEmoji"><el-icon size="14"><DeleteOutlined /></el-icon></x-button>
        </div>
        <div class="recommended-section mb-2">
          <div class="text-[16px] font-medium py-[5px] px-[6px]">{{ t('common.recommended') }}</div>
          <div class="grid grid-cols-9 gap-1">
            <span v-for="e in recommendedEmojis" :key="e" class="text-[20px] cursor-pointer hover:bg-gray-100 rounded p-1 text-center" @click="selectEmoji(e)">{{ e }}</span>
          </div>
        </div>
        <div class="border-t pt-2">
          <Picker :data="emojiIndex" :emoji-size="24" :per-line="9" :show-preview="false" :show-skin-tones="false" :native="true" @select="onPickerSelect" />
        </div>
      </div>
    </template>
  </el-dropdown>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ChevronDown, DeleteOutlined } from '@xpos/icons-svg'
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast/src'
import data from 'emoji-mart-vue-fast/data/all.json'
import 'emoji-mart-vue-fast/css/emoji-mart.css'
import { LEGACY_CATEGORY_ICON_MAP } from '@/constants/category'
import type { ElDropdown } from 'element-plus'

const { t } = useI18n()
const props = defineProps<{ modelValue?: string }>()
const emit = defineEmits<{ 'update:modelValue': [value: string]; change: [value: string] }>()

const dropdownRef = ref<InstanceType<typeof ElDropdown>>()
const emojiIndex = new EmojiIndex(data)

const recommendedEmojis = [
  '🍹', '☕', '🍺', '🍷', '🍵', '🍜', '🍚', '🍲', '🥗',
  '🍖', '🦐', '🥬', '🍰', '🍞', '🍳', '🍔', '🍕', '🥡',
  '🎁', '⭐', '🍽️', '🥢', '🛒', '✂️', '🎉', '👶', '🔥'
]

const displayEmoji = computed(() => {
  if (!props.modelValue) return ''
  return LEGACY_CATEGORY_ICON_MAP[props.modelValue] ?? props.modelValue
})

const selectEmoji = (emoji: string) => {
  emit('update:modelValue', emoji)
  emit('change', emoji)
}

// Clear selection and close the dropdown
const clearEmoji = () => {
  emit('update:modelValue', '')
  emit('change', '')
  dropdownRef.value?.handleClose()
}

const onPickerSelect = (emoji: { native: string }) => selectEmoji(emoji.native)
</script>
<style scoped>
.emoji-trigger {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  height: 48px;
  border: 1px solid #a8a8a8;
  width: 100%;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
}
.emoji-picker-container {
  padding: 10px;
  width: 380px;
}
.emoji-picker-container :deep(.emoji-mart) {
  border: none;
  width: 100% !important;
}
.emoji-picker-container :deep(.emoji-mart-bar) {
  display: none;
}
.emoji-picker-container :deep(.emoji-mart-search input) {
  border-radius: 8px;
  border: 1px solid #a8a8a8;
  height: 40px;
  &:focus { box-shadow: 0 0 0 2px var(--el-color-primary) inset; border-color: var(--el-color-primary); }
}
.emoji-picker-container :deep(.emoji-mart-scroll) {
  height: 250px;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}
</style>
